import { useCallback, useMemo, type ReactNode } from 'react';
import { columnId } from 'components/data-table';
import { useExportLifecycleToast } from 'lib/hooks/useExportLifecycleToast';
import { useTypeCurveListingExport } from './hooks';
import { typeCurvesListingColumns } from './type-curves-listing-columns';
import type { TypeCurveHeadersQueryParams } from './atoms';

export interface TypeCurveListingExcelItem {
  /** The menu-item label; flips to an in-progress label while the export runs. */
  label: string;
  /** Disabled while loading, when nothing matches, or while an export is already in flight. */
  disabled: boolean;
  /** Starts the async listing export (no-op if already running or params not loaded). */
  onClick: () => void;
  /** The lifecycle toast node to render beside the menu (null when idle/dismissed). */
  toast: ReactNode;
}

/**
 * The "Excel" item of the Type Curves Export dropdown: the async server export of the filtered listing,
 * with its lifecycle toast and in-progress label. Pulled out of the menu component so the menu body stays
 * the trigger's disabled logic + JSX, and so the toast reuses the shared {@link useExportLifecycleToast}
 * rather than a fourth hand-rolled `toastAtom` copy.
 *
 * @param {TypeCurveHeadersQueryParams | undefined} params - The shared catalog filters, including the
 *   table's free-text search.
 * @param {number} count - The filtered record count (0 disables the item).
 * @param {boolean} loading - True until the listing's filters/count have loaded (disables the item).
 * @param {boolean} fetching - True while the listing fetches (a filter, search, sort or page change):
 *   the table still shows the previous rows, so `params` and `count` can describe different sets.
 *   The item holds and says so on its label until the fetch lands.
 * @return {TypeCurveListingExcelItem} The item's label, disabled state, click handler, and toast node.
 */
export function useTypeCurveListingExcelItem(
  params: TypeCurveHeadersQueryParams | undefined,
  count: number,
  loading: boolean,
  fetching = false
): TypeCurveListingExcelItem {
  // Destructure the fields the item uses: `useServerExport` returns a fresh object each render, so
  // depending on `startExport`/`isExporting` (not the whole object) keeps the click handler stable.
  const { startExport, isExporting, exportStatus } =
    useTypeCurveListingExport();

  // The listing export covers the columns the table shows, in display order. The listing header has no
  // column-hide control today, so every column is visible. `columnId` resolves the explicit id, else the
  // string accessorKey — the same rule TanStack uses — so this is a static, non-empty list.
  const visibleColumns = useMemo(
    () => typeCurvesListingColumns.map(columnId),
    []
  );

  const disabled = loading || fetching || count === 0 || isExporting;
  // A menu item can't show an inline spinner, so surface the in-flight run on the label itself — the
  // lifecycle toast's "started" message self-dismisses, this stays for the whole multi-second run.
  // The same goes for a listing fetch: the label says why the item is held.
  const label = isExporting
    ? 'Exporting…'
    : fetching
      ? 'Excel (loading…)'
      : 'Excel';

  const onClick = useCallback(() => {
    if (isExporting || !params) return;
    // The params carry the table's free-text search as `text`; the export endpoint runs the table's own
    // query (DetailsElastic), so the search scopes the workbook exactly as it scopes the table.
    void startExport({ queryParams: params, visibleColumns });
  }, [startExport, isExporting, params, visibleColumns]);

  const toast = useExportLifecycleToast(
    exportStatus.status,
    exportStatus.error,
    {
      processing:
        'Export started. Your file will download automatically when ready.',
      complete: 'Your Type Curves data has been exported.',
    }
  );

  return { label, disabled, onClick, toast };
}
